import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    Stack,
    Alert,
    CircularProgress,
    Pagination,
    Card,
    IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import LocalPostOfficeIcon from "@mui/icons-material/LocalPostOffice";
import EuroIcon from "@mui/icons-material/Euro";
import { mapTransportistasAOpciones } from "../../utils/transportistas";
import { SIN_TRANSPORTISTA_ASIGNADO } from "../../constants";

/**
 * Vista mobile con lista paginada de rangos de precios
 *
 * onEditar/onEliminar reciben la fila completa, la misma firma que la columna
 * de acciones de helpers/columns.js, para que la pagina pueda pasar el mismo
 * handler a las dos vistas sin envoltorios
 */
export default function RangosMobileList({ rows, loading, transportistas, onEditar, onEliminar }) {
    const [page, setPage] = useState(1);
    const pageSize = 10;

    // Si cambian los rows (filtros), vuelve a la primera página
    useEffect(() => {
        setPage(1);
    }, [rows]);

    // Reutiliza el mismo mapeo que helpers/columns.js (valueOptions del DataGrid)
    // para no duplicar la construcción de la etiqueta "CODIGO-NOMBRE"
    const opcionesTransportista = useMemo(
        () => mapTransportistasAOpciones(transportistas),
        [transportistas]
    );

    const transportistaPorCodigo = useMemo(
        () => new Map(opcionesTransportista.map((opcion) => [opcion.value, opcion.label])),
        [opcionesTransportista]
    );

    const getTransportistaLabel = (idAgencia) =>
        transportistaPorCodigo.get(idAgencia || SIN_TRANSPORTISTA_ASIGNADO) || "Sin asignar";

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: "#cc071e" }} />
            </Box>
        );
    }

    if (!rows?.length) {
        return (
            <Alert severity="info" sx={{ mt: 2 }}>
                No hay rangos de precios
            </Alert>
        );
    }

    const totalPages = Math.ceil(rows.length / pageSize);
    const start = (page - 1) * pageSize;
    const pageRows = rows.slice(start, start + pageSize);

    return (
        <Box sx={{ width: "100%" }}>
            <Stack spacing={2}>
                {pageRows.map((rango) => {
                    const transportistaLabel = getTransportistaLabel(rango.idAgencia);
                    const sinTransportista = transportistaLabel === "Sin asignar";

                    return (
                        <Card
                            key={rango.id}
                            sx={{
                                p: 2,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            }}
                        >
                            <Stack spacing={1.5}>
                                {/* Isla en texto plano: el badge de color se retiro a peticion
                                    del usuario tras probarlo (ver notes de TASK-012) */}
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <LocationOnIcon fontSize="small" color="action" />
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        {rango.isla || "-"}
                                    </Typography>
                                </Stack>

                                {/* Transportista */}
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <LocalShippingIcon fontSize="small" color="action" />
                                    <Typography
                                        variant="body2"
                                        sx={{ color: sinTransportista ? "#6c757d" : "text.secondary" }}
                                    >
                                        {transportistaLabel}
                                    </Typography>
                                </Stack>

                                {/* Rango de códigos postales */}
                                <Stack direction="row" alignItems="flex-start" spacing={1}>
                                    <LocalPostOfficeIcon fontSize="small" color="action" sx={{ mt: 0.2 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        {rango.rango || "-"}
                                    </Typography>
                                </Stack>

                                {/* Precio bulto y precio palet */}
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <EuroIcon sx={{ fontSize: 14, color: "#6c757d" }} />
                                        <Typography sx={{ fontSize: 12, color: "#6c757d", fontWeight: 600 }}>
                                            Bulto:
                                        </Typography>
                                        <Typography sx={{ fontSize: 13, color: "#495057", fontWeight: 600 }}>
                                            {rango.precio ?? "-"}
                                        </Typography>
                                    </Stack>
                                    <Typography sx={{ fontSize: 12, color: "#6c757d" }}>·</Typography>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Typography sx={{ fontSize: 12, color: "#6c757d", fontWeight: 600 }}>
                                            Palet:
                                        </Typography>
                                        <Typography sx={{ fontSize: 13, color: "#495057", fontWeight: 600 }}>
                                            {rango.palet ?? "-"}
                                        </Typography>
                                    </Stack>
                                </Stack>

                                {/* Botones de acción */}
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <IconButton
                                        size="small"
                                        onClick={() => onEditar(rango)}
                                        aria-label="Editar rango"
                                        sx={{ color: "#cc071e" }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => onEliminar(rango)}
                                        aria-label="Eliminar rango"
                                        sx={{ color: "error.main" }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Card>
                    );
                })}
            </Stack>

            {/* Paginación (no pedida por los criterios de aceptación; añadida por
                consistencia con RecogidasMobileList/TraspasosMobileList, ya que la
                lista de rangos puede crecer bastante) */}
            {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        color="primary"
                    />
                </Box>
            )}
        </Box>
    );
}
